import { useIsDarkScheme } from "@faro-lotv/flat-ui";
import { logisticFunction } from "@faro-lotv/foundation";
import { create } from "zustand";

export type PointOpacitySettings = {
  minOpacity: number;
  maxOpacity: number;
  midPoint: number;
  growthRate: number;
};

// Values determined experimentally to give good visual quality for a wide range of projects.
// The goal is to keep walls and other features clearly visible even when zoomed out,
// while still making individual points visible when zoomed in.
const defaultLightSettings: PointOpacitySettings = Object.freeze({
  minOpacity: 0.008,
  maxOpacity: 0.4,
  midPoint: 3.5,
  // Inverted growth rate as lower points / px should result in higher opacity
  growthRate: -0.55,
});

// Faint points vanish against the dark background
const defaultDarkSettings: PointOpacitySettings = Object.freeze({
  ...defaultLightSettings,
  minOpacity: 0.02,
});

export type PointOpacitySettingsStore = {
  light: PointOpacitySettings;
  dark: PointOpacitySettings;
  updateSettings(
    isDarkMode: boolean,
    settings: Partial<PointOpacitySettings>,
  ): void;
  resetToDefault(isDarkMode: boolean): void;
};

/** Context containing settings for dynamically setting the point opacity, per theme. */
export const usePointOpacitySettingsContext = create<PointOpacitySettingsStore>(
  (set) => ({
    light: defaultLightSettings,
    dark: defaultDarkSettings,
    updateSettings: (isDarkMode, settings) =>
      set((state) =>
        isDarkMode
          ? { dark: { ...state.dark, ...settings } }
          : { light: { ...state.light, ...settings } },
      ),
    resetToDefault: (isDarkMode) =>
      set(
        isDarkMode
          ? { dark: defaultDarkSettings }
          : { light: defaultLightSettings },
      ),
  }),
);

/** @returns The point opacity settings for the active theme. */
export function useActivePointOpacitySettings(): PointOpacitySettings {
  const isDarkMode = useIsDarkScheme();
  return usePointOpacitySettingsContext((state) =>
    isDarkMode ? state.dark : state.light,
  );
}

/** @returns A function to convert maximum point density to the opacity to use per point. */
export function pointOpacityCurve({
  minOpacity,
  maxOpacity,
  midPoint,
  growthRate,
}: PointOpacitySettings): (pointDensity: number) => number {
  const innerFn = logisticFunction({
    capacity: maxOpacity,
    growthRate,
    midPoint,
  });

  return (pointDensity: number): number =>
    Math.max(minOpacity, innerFn(pointDensity));
}
